import { Space } from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useAuth } from "src/core/hooks/useAuth";
import { routingsService } from "src/core/services/costExplosionServices/routingsService";
import { useRoutingsSummaryColumns } from "src/modules/CostExplosionManager/tabs/RoutingsTab/components/RoutingsSummaryTable/hooks/useRoutingsSummaryColumns";
import { useRoutingsSummaryDataItemData } from "src/modules/CostExplosionManager/tabs/RoutingsTab/components/RoutingsSummaryTable/hooks/useRoutingsSummaryDataItemData";
import { TableColumnsSelector } from "src/modules/TableColumnsSelector/TableColumnsSelector";
import { appParamsState } from "src/recoil/atoms";
import {
  costExplosionAppState,
  costExplosionGlobalState,
} from "src/recoil/costExplosionAtoms";
import { RoutingsDataItem } from "src/types/costExplosion";

export const RoutingsSummaryTable: React.FC = () => {
  const [tableLoading, setTableLoading] = useState(false);

  const { accessToken, success, authLoaded } = useAuth();

  const [dataItems, setDataItems] = useState<RoutingsDataItem[]>([]);

  const appParams = useRecoilValue(appParamsState);

  const costExplosionAppStateData = useRecoilValue(costExplosionAppState);

  const costExplosionState = useRecoilValue(costExplosionGlobalState);

  const { processed, processedData } =
    useRoutingsSummaryDataItemData(dataItems);
  const { getTableColumns } = useRoutingsSummaryColumns(processedData);

  const [tableColumns, setTableColumns] = useState<
    ColumnsType<RoutingsDataItem>
  >([]);
  const [filteredTableColumns, setFilteredTableColumns] = useState<
    ColumnsType<RoutingsDataItem>
  >([]);

  useEffect(() => {
    (async () => {
      setTableColumns(getTableColumns());
      setFilteredTableColumns(getTableColumns());
    })();
  }, [getTableColumns]);

  useEffect(() => {
    (async () => {
      if (authLoaded && success) {
        if (!costExplosionAppStateData.selectingModelSku) {
          if (
            costExplosionState.selectedModelSkus &&
            costExplosionState.selectedModelSkus?.length > 0
          ) {
            setTableLoading(true);

            let skusList: string[] = [];

            costExplosionState.selectedModelSkus.forEach((m) => {
              if (m.sku) {
                skusList.push(m.sku);
              }
            });

            if (skusList.length > 0) {
              const response =
                await routingsService.fetchRoutingsSummaryDataItems(
                  accessToken,
                  costExplosionState.modelNumber ?? "",
                  skusList,
                  costExplosionState.adjustmentsEnabled
                );

              setDataItems(response);
            }

            setTableLoading(false);
          }
        }
      }
    })();
  }, [
    accessToken,
    authLoaded,
    costExplosionAppStateData.selectingModelSku,
    costExplosionState.adjustmentsEnabled,
    costExplosionState.modelNumber,
    costExplosionState.selectedModelSkus,
    success,
  ]);

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <TableColumnsSelector
        baseColumns={tableColumns as []}
        setTableColumns={(columnsList) => {
          setFilteredTableColumns(columnsList as ColumnsType<RoutingsDataItem>);
        }}
      />
      <Table
        columns={filteredTableColumns}
        loading={tableLoading || !processed}
        dataSource={processedData}
        pagination={false}
        rowKey={({ rowKey }) => `${rowKey}`}
        sticky={true}
        scroll={{ x: "max-content", y: 480 }}
        style={{ width: appParams.contentWidth - 60 }}
        rowClassName={(row) =>
          (row.rowType ?? null) !== null ? `row-type-class-${row.rowType}` : ""
        }
        size={"small"}
        bordered
      />
    </Space>
  );
};
